/* ==========================================================================
   layaway-form.js — Layby USA checkout controller
   Built against sections/main-cart.liquid (binds by element id).

   Ported from the AU controller (prestige-au-base:assets/page-form.es6.js),
   rewritten vanilla — Hyper ships no jQuery. AU's BSB direct-debit form was
   dropped; step machinery, payday chips, tax quote, Reg E authorization
   rendering and the 3DS return leg are new.

   TWO RAILS since 2026-07-31. The Payment Element renders whatever the CRM's
   SetupIntent allows — today card (listed first, the default) and US bank
   account via ACH. This file does not choose or even name the rail: it hands
   the client_secret to Stripe, and the CRM learns which instrument came back
   when it vaults the SetupIntent. That is why the copy here says "payment
   details" rather than "card". Bank accounts are verified instantly through
   Financial Connections; a setup that does not reach 'succeeded' is refused
   rather than carried into an order (see the confirmSetup handler).

   Nothing is taken at signup: the instrument is verified with a $0
   authorization and the first installment lands on the payday the customer
   picks. The plan JSON still carries deposit_cents (it is 0) — we read it
   rather than assume, so a future non-zero value renders instead of being
   silently hidden.

   All money math comes from the schedule-data JSON emitted by
   snippets/layaway-plan.liquid — never recompute plan math here.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.LAYAWAY_CONFIG || {};
  /* Region seam. assets/layaway-region.js sets window.LAYAWAY_REGION before this
     file runs: validators, masks, date order, the payday rule, the rail and the
     payload shape. Every read below falls back to the US behaviour, so a page
     without the region file behaves exactly as this file did before the seam. */
  var REGION = window.LAYAWAY_REGION || {};
  var RGN = function (key, fallback) { return REGION[key] !== undefined ? REGION[key] : fallback; };
  var ADDRESS = REGION.address || {};
  var PHONE = REGION.phone || {};
  var POSTCODE = REGION.postcode || {};
  var DATE_ORDER = RGN('dateOrder', 'MDY'); // how slash dates are written on the wire
  var STORAGE_KEY = 'layaway_form_data';
  var PENDING_KEY = 'ladPendingOrder';
  // The publishable key the CRM served with the SetupIntent, kept across the 3DS
  // redirect — that return lands on a fresh page load with no CRM round-trip, and
  // retrieveSetupIntent must use the SAME account's key or it cannot read the intent.
  var PK_KEY = 'ladStripePk';
  var EXIT_KEY = 'layaway_exit_shown';

  var form = document.getElementById('layaway-signup');
  if (!form || form.layawayAbandoned || form.layawayInitializing || form.layawayReady) return;
  form.layawayInitializing = true;
  // These regions own bank validation, dates, payloads and payment arithmetic.
  // Missing code must not silently fall back to the USA checkout behavior.
  if (REGION.code !== form.dataset.layawayRegion || typeof REGION.buildPayload !== 'function' ||
      !window.LaybyPlan || typeof window.LaybyPlan.laybyPlan !== 'function') {
    throw new Error('Regional checkout dependencies are missing or invalid');
  }

  /* ---------------------------------------------------------------- utils */

  var $ = function (id) { return document.getElementById(id); };
  var money = function (cents) {
    if (cents == null || isNaN(cents)) return '—';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Day-month order follows the region's date order: "Sep 25" in the US, "25 Sep" where dates are written DMY.
  var fmtDate = function (d) { return DATE_ORDER === 'DMY' ? d.getDate() + ' ' + MONTHS[d.getMonth()] : MONTHS[d.getMonth()] + ' ' + d.getDate(); };
  var fmtLong = function (d) { return DAYS[d.getDay()] + ', ' + fmtDate(d); };
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var fmtShort = function (d) { return DAYS_SHORT[d.getDay()] + ', ' + fmtDate(d); };
  // Month + day, with the year appended only once the date leaves the current
  // year — a 140-week plan runs into 2029, and "Paid off Mar 17" alone would
  // read as next spring.
  var fmtDateYear = function (d) {
    var y = d.getFullYear();
    return fmtDate(d) + (y !== new Date().getFullYear() ? ', ' + y : '');
  };
  var fmtShortYear = function (d) {
    var y = d.getFullYear();
    return fmtShort(d) + (y !== new Date().getFullYear() ? ', ' + y : '');
  };
  var fmtSlash = function (d) {
    var mm = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
    return (DATE_ORDER === 'DMY' ? dd + '/' + mm : mm + '/' + dd) + '/' + d.getFullYear();
  };
  var parseSlash = function (s) {
    var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || '');
    if (!m) return null;
    var month = DATE_ORDER === 'DMY' ? +m[2] : +m[1];
    var day = DATE_ORDER === 'DMY' ? +m[1] : +m[2];
    var d = new Date(+m[3], month - 1, day);
    // reject 31/02 style dates — Date() would roll them into March
    if (isNaN(d.getTime()) || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return d;
  };
  var addDays = function (d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; };
  var startOfToday = function () { var d = new Date(); d.setHours(0, 0, 0, 0); return d; };

  /* ------------------------------------------------------------ plan data */

  var PLAN = (function () {
    var node = form.querySelector('script[data-layaway-plan]');
    var plan = node && JSON.parse(node.textContent);
    var keys = ['price_cents', 'deposit_cents', 'installment_cents', 'weekly_base_cents',
      'deposit_pct', 'deposit_floor_cents', 'order_cap_cents'];
    if (!plan || !keys.every(function (key) {
      return typeof plan[key] === 'number' && isFinite(plan[key]) && plan[key] >= 0;
    }) || plan.weekly_base_cents <= 0 || plan.order_cap_cents <= 0) {
      throw new Error('Checkout plan data is missing or invalid');
    }
    return plan;
  })();

  // What the customer pays at signup: nothing. deposit_cents comes from the
  // plan JSON and is 0, so this reads "$0"; a non-zero value would render as
  // real money instead of being hidden.
  var dueTodayLabel = function () {
    return PLAN.deposit_cents ? money(PLAN.deposit_cents) : '$0';
  };
  var submitLabel = function () {
    // The button's own text (rendered by Liquid with the region's noun) is the
    // template; only the money figure is swapped.
    var btn = document.getElementById('btn-submit');
    var txt = btn && btn.querySelector('.btn__text');
    var tpl = (txt && txt.getAttribute('data-label')) || '';
    if (!tpl && txt) { tpl = txt.textContent.replace(/\s+/g, ' ').trim(); txt.setAttribute('data-label', tpl); }
    if (!tpl) return 'Start my plan — ' + dueTodayLabel() + ' today';
    return tpl.replace(/\$[\d,]+(?:\.\d{2})?/, dueTodayLabel());
  };

  var state = {
    step: 1,
    frequency: 'weekly',
    payday: null,        // Date
    taxCents: null,
    taxQuoteId: null,
    stripe: null,
    elements: null,
    publishableKey: null,
    stripeMounted: false,
    placesBound: false,
    submitting: false
  };

  // Weekly is the base cadence: the plan is $25 a week and PLAN.weeks is the
  // weekly payment count. Other cadences are the SAME total over the SAME
  // elapsed time, just fewer, larger payments — the count and amount are both
  // derived from cadence days so the arithmetic cannot drift from the plan.
  // Every cadence is a whole number of WEEKS. Semi-monthly (24/yr) never lines up with a
  // 7-day grid and was removed 2026-07-28; calendar-monthly is out for the same reason
  // plus two more — a 30-day step drifts the weekday, so a plan that starts on a payable
  // Wed/Thu/Fri would wander onto Mon/Tue and weekends, and $25 x (30/7) is 10714.2857
  // cents, which the server's own sum assertion rejects. "Every 4 weeks" is 28 days: the
  // same idea, exact cents, and the weekday holds for all 13 payments.
  var CADENCE = RGN('cadences', {
    weekly: { days: 7, label: 'every week' },
    biweekly: { days: 14, label: 'every 2 weeks' },
    fourweekly: { days: 28, label: 'every 4 weeks' }
  });

  /* --------------------------------------------------------- field errors */

  var ERROR_MAP = {
    'order-form-register-email': 'email-error',
    'order-form-register-phone': 'phone-error',
    'order-form-register-f-name': 'fname-error',
    'order-form-register-l-name': 'lname-error',
    'dob': 'dob-error',
    'order-form-register-street-address': 'address-error',
    'order-form-register-city': 'city-error',
    'province': 'state-error',
    'order-form-register-zip': 'zip-error',
    'order-form-pay-frequency': 'frequency-error',
    'next-pay': 'payday-error',
    'agreed_to_electronic_records': 'esign-error',
    'agreed_to_payment_info': 'auth-error',
    'typed-signature': 'signature-error',
    'agreed_to_terms': 'terms-error',
    'agreed_to_cancellation': 'cancellation-error',
    // the gift — validated only while the toggle is on
    'gift-f-name': 'gift-fname-error',
    'gift-l-name': 'gift-lname-error',
    'gift-phone': 'gift-phone-error',
    'gift-street-address': 'gift-address-error',
    'gift-city': 'gift-city-error',
    'gift-province': 'gift-state-error',
    'gift-zip': 'gift-zip-error'
  };
  Object.keys(REGION.errorMap || {}).forEach(function (k) { ERROR_MAP[k] = REGION.errorMap[k]; });

  function setError(fieldId, message) {
    var span = $(ERROR_MAP[fieldId]);
    if (span) span.textContent = message || '';
    var el = $(fieldId);
    if (el && el.classList) el.classList.toggle('has-error', !!message);
  }
  function clearErrors() {
    Object.keys(ERROR_MAP).forEach(function (id) { setError(id, ''); });
  }

  /* ----------------------------------------------------------- validation */

  var RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function validateField(id) {
    var el = $(id);
    if (!el) return true;
    var v = (el.value || '').trim();

    // A region's own field (a bank account, a BSB) validates through its hook.
    if (REGION.validators && typeof REGION.validators[id] === 'function') {
      var regionMsg = REGION.validators[id](v, el, H);
      if (regionMsg) return setError(id, regionMsg), false;
      setError(id, '');
      return true;
    }

    switch (id) {
      case 'order-form-register-email':
        if (!v) return setError(id, 'Enter your email so we can send your plan.'), false;
        if (!RE_EMAIL.test(v)) return setError(id, 'That email doesn\'t look right.'), false;
        break;
      case 'order-form-register-phone': {
        if (!v) return setError(id, 'Enter your mobile number.'), false;
        // Real NANP shape, matching the server: area code and exchange start
        // 2-9. "10 digits" alone accepts numbers no US carrier can issue (an
        // NZ 027... mobile is 10 digits) and the failure then surfaces as a
        // generic server error at the very last step.
        if (!phoneOk(v)) return setError(id, phoneHint()), false;
        break;
      }
      case 'order-form-register-f-name':
        if (v.length < 2) return setError(id, 'Enter your first name.'), false;
        break;
      case 'order-form-register-l-name':
        if (v.length < 2) return setError(id, 'Enter your last name.'), false;
        break;
      case 'dob': {
        var d = parseSlash(v);
        if (!d) return setError(id, (REGION.dob && REGION.dob.hint) || 'Use MM/DD/YYYY.'), false;
        var age = (Date.now() - d.getTime()) / 31557600000;
        if (age < 18) return setError(id, 'You must be 18 or older to start a plan.'), false;
        if (age > 110) return setError(id, 'Check your date of birth.'), false;
        break;
      }
      case 'order-form-register-street-address':
        if (v.length < 4) return setError(id, 'Enter your street address.'), false;
        break;
      case 'order-form-register-city':
        if (v.length < 2) return setError(id, 'Enter your city.'), false;
        break;
      case 'province':
        if (!v) return setError(id, ADDRESS.stateHint || 'Choose your state.'), false;
        // Compare the CODE, not the value — values are full names, so the old
        // `v === 'DC'` test never matched anything. A region with no allow-list
        // (null) gates nothing: any option in the select is a place we serve.
        if (ALLOWED_STATE_CODES && ALLOWED_STATE_CODES.indexOf(selectedStateCode()) === -1) {
          return setError(id, ADDRESS.stateBlockedHint || 'We can\'t serve this state yet.'), false;
        }
        break;
      case 'order-form-register-zip':
        if (!postcodeOk(v)) return setError(id, postcodeHint()), false;
        break;
      case 'next-pay': {
        if (!v) return setError(id, 'Pick the day you would like to pay.'), false;
        // Presence alone was the whole check. A restored cart or a hand-edited field
        // could carry a Monday, which the server then silently moves to Wednesday —
        // the customer signs one date and is debited another.
        var picked = parseSlash(v);
        if (!picked || !isPayableDay(picked)) {
          return setError(id, RGN('payableDayHint', 'Payments come out on a Wednesday, Thursday or Friday.')), false;
        }
        break;
      }
      case 'typed-signature':
        if (v.length < 3) return setError(id, 'Type your full name to sign.'), false;
        if (sigPad && !sigPad.hasInk()) {
          setError('sigpad-error', 'Please sign in the box above.');
          return false;
        }
        break;
      case 'agreed_to_electronic_records':
      case 'agreed_to_payment_info':
      case 'agreed_to_terms':
      case 'agreed_to_cancellation':
        if (!el.checked) return setError(id, 'Please check this box to continue.'), false;
        break;

      // ---- the gift. Same shapes as the payer's own fields; different words.
      case 'gift-f-name':
        if (v.length < 2) return setError(id, 'Enter their first name.'), false;
        break;
      case 'gift-l-name':
        if (v.length < 2) return setError(id, 'Enter their last name.'), false;
        break;
      case 'gift-phone': {
        if (!v) return setError(id, 'Enter their mobile number, for the courier.'), false;
        if (!phoneOk(v)) return setError(id, phoneHint()), false;
        break;
      }
      case 'gift-street-address':
        if (v.length < 4) return setError(id, 'Enter their street address.'), false;
        break;
      case 'gift-city':
        if (v.length < 2) return setError(id, 'Enter their city.'), false;
        break;
      case 'gift-province':
        if (!v) return setError(id, 'Choose their state.'), false;
        if (ALLOWED_STATE_CODES && ALLOWED_STATE_CODES.indexOf(codeOf('gift-province')) === -1) {
          return setError(id, 'We can\'t deliver to this state yet.'), false;
        }
        break;
      case 'gift-zip':
        if (!postcodeOk(v)) return setError(id, postcodeHint()), false;
        break;
    }
    setError(id, '');
    return true;
  }

  /* Phone and postcode rules are the region's. The US shapes are the defaults:
     a real NANP number (area code and exchange 2-9, a leading 1 tolerated) and
     a 5-digit ZIP. */
  function phoneOk(v) {
    if (typeof PHONE.test === 'function') return !!PHONE.test(v);
    var pd = v.replace(/\D/g, '');
    if (pd.length === 11 && pd.charAt(0) === '1') pd = pd.slice(1);
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(pd);
  }
  function phoneHint() { return PHONE.hint || 'Enter a valid US mobile number \u2014 10 digits, area code starting 2\u20139.'; }
  function postcodeOk(v) {
    if (typeof POSTCODE.test === 'function') return !!POSTCODE.test(v);
    return /^\d{5}$/.test(v);
  }
  function postcodeHint() { return POSTCODE.hint || 'Enter a 5-digit ZIP code.'; }

  /** The USPS code behind whichever state <select> is asked about. */
  function codeOf(selectId) {
    var sel = $(selectId);
    if (!sel || sel.selectedIndex < 0) return '';
    return sel.options[sel.selectedIndex].getAttribute('data-code') || '';
  }

  function giftOn() {
    var t = $('gift-toggle');
    return !!(t && t.checked);
  }

  var STEP_FIELDS = {
    1: ['order-form-register-email', 'order-form-register-phone'],
    2: ['order-form-register-f-name', 'order-form-register-l-name', 'dob',
        'order-form-register-street-address', 'order-form-register-city', 'province', 'order-form-register-zip'],
    3: ['next-pay'],
    4: RGN('step4Fields', ['agreed_to_electronic_records', 'agreed_to_payment_info', 'typed-signature',
        'agreed_to_terms', 'agreed_to_cancellation']),
    gift: ['gift-f-name', 'gift-l-name', 'gift-phone', 'gift-street-address', 'gift-city', 'gift-province', 'gift-zip']
  };

  function validateStep(n) {
    var ok = true, firstBad = null;
    STEP_FIELDS[n].forEach(function (id) {
      if (!validateField(id)) { ok = false; if (!firstBad) firstBad = id; }
    });
    if (!ok && firstBad) {
      var el = $(firstBad);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.focus) try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
      }
    }
    return ok;
  }

  /* ------------------------------------------------------------- sections

     The form was four steps behind three Continue buttons. It is one page
     now, so nothing is ever hidden and there is nothing to advance TO: what
     is left of goToStep is scrolling to a section, which is what the Stripe
     error paths actually wanted when they asked for step 4.

     The step numbers stay because validation is still grouped by them, and
     because the fieldsets are still sections a customer reads in an order.
  */

  function goToStep(n) {
    state.step = n;
    if (checkoutCapture) checkoutCapture.schedule();
    var fs = form.querySelector('.layaway-step[data-step="' + n + '"]');
    var top = (fs || form).getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
    persist();
  }

  /* -------------------------------------------------------------- masking */

  function bindMask(id, fn) {
    var el = $(id);
    if (!el) return;
    el.addEventListener('input', function () {
      var pos = el.selectionStart === el.value.length;
      el.value = fn(el.value);
      if (pos) el.selectionStart = el.selectionEnd = el.value.length;
    });
  }
  bindMask('dob', function (v) {
    var d = v.replace(/\D/g, '').slice(0, 8);
    if (d.length > 4) return d.slice(0, 2) + '/' + d.slice(2, 4) + '/' + d.slice(4);
    if (d.length > 2) return d.slice(0, 2) + '/' + d.slice(2);
    return d;
  });
  var phoneMask = PHONE.mask || function (v) {
    var d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length > 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
    if (d.length > 3) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
    if (d.length) return '(' + d;
    return d;
  };
  var postcodeMask = POSTCODE.mask || function (v) { return v.replace(/\D/g, '').slice(0, 5); };
  bindMask('order-form-register-phone', phoneMask);
  bindMask('order-form-register-zip', postcodeMask);
  bindMask('gift-phone', phoneMask);
  bindMask('gift-zip', postcodeMask);

  // inline validation on blur
  Object.keys(ERROR_MAP).forEach(function (id) {
    var el = $(id);
    if (!el) return;
    var evt = (el.type === 'checkbox' || el.tagName === 'SELECT') ? 'change' : 'blur';
    el.addEventListener(evt, function () { validateField(id); persist(); });
  });

  /* --------------------------------------- excluded-jurisdiction gating */

  // Since the 2026-09-11 terms we serve FIVE states (§10/§16): the <select>
  // lists only those, which is the real block; this gate is the belt to that
  // braces, and it is an ALLOW-list so an unexpected code fails closed.
  //
  // It reads data-code, NOT sel.value. The option values are FULL NAMES
  // ("Ohio"), so the previous `sel.value === 'DC' || sel.value === 'MD'` could
  // never be true and this gate had never fired once. Nothing depended on it
  // only because the omission above was doing the work.
  var ALLOWED_STATE_CODES = ADDRESS.allowedStateCodes === undefined ? ['AR', 'MI', 'OK', 'PA', 'TX'] : ADDRESS.allowedStateCodes;

  function selectedStateCode() {
    var sel = $('province');
    if (!sel || sel.selectedIndex < 0) return '';
    var opt = sel.options[sel.selectedIndex];
    return (opt && opt.getAttribute('data-code')) || '';
  }

  /**
   * Set the state <select> from a USPS code, via each option's data-code.
   *
   * Returns false when no option carries that code — which is exactly what
   * happens for every state outside the five we serve, because only those five
   * are in the markup. The dropdown IS the allowlist, so "Google returned a
   * state we do not stock an option for" and "we do not serve that state" are
   * the same condition, and there is no second list to keep in step.
   */
  function setStateByCode(code, selectId) {
    var sel = $(selectId || 'province');
    if (!sel || !code) return false;
    var want = String(code).toLowerCase();
    for (var pass = 0; pass < 2; pass++) {
      for (var i = 0; i < sel.options.length; i++) {
        var opt = sel.options[i];
        var hit = pass === 0
          ? opt.getAttribute('data-code') === code
          : (opt.value && opt.value.toLowerCase() === want) || (opt.text && opt.text.toLowerCase() === want);
        if (hit) {
          sel.selectedIndex = i;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  /**
   * A state we cannot serve used to disable the Continue button between
   * step 2 and step 3. There is no Continue any more, so it disables the one
   * button left — alongside the order cap, which blocks the same button for a
   * different reason and must not be cleared by this one.
   */
  function applySubmitBlocks() {
    var btn = $('btn-submit');
    if (btn) btn.disabled = !!(!form.layawayReady || form.layawayAbandoned || state.submitting || state.stateBlocked || state.capBlocked || state.emptyCart || (giftOn() && state.giftStateBlocked));
  }

  function checkStateGate() {
    var sel = $('province');
    var msg = $('state-gate-message');
    var code = selectedStateCode();
    var blocked = !!(sel && (sel.dataset.unsupportedState || (sel.value && ALLOWED_STATE_CODES && ALLOWED_STATE_CODES.indexOf(code) === -1)));
    if (msg) msg.hidden = !blocked;
    state.stateBlocked = blocked;
    applySubmitBlocks();
    return !blocked;
  }
  if ($('province')) $('province').addEventListener('change', function () {
    if (selectedStateCode()) delete $('province').dataset.unsupportedState;
    checkStateGate(); maybeQuoteTax();
  });
  if ($('order-form-register-zip')) $('order-form-register-zip').addEventListener('blur', maybeQuoteTax);

  /* -------------------------------------------- Google Places autocomplete */

  /**
   * Address autocomplete, ported from the AU controller and restricted to the US.
   *
   * Differences from AU that matter:
   *   - componentRestrictions country 'us'.
   *   - The state <select> stores FULL NAMES as option values while Google
   *     returns a short_name ('OH'), so the code is matched against each
   *     option's data-code rather than assigned to .value.
   *   - An excluded jurisdiction has no option at all, so a Google pick in any
   *     state we do not serve lands on the state gate instead of silently leaving the
   *     select empty. That is the case AU never had.
   *   - subpremise goes to the Apt/Unit field, which exists separately here,
   *     rather than being folded into street address.
   *
   * Every write dispatches input/change so the existing validation and the
   * sales-tax quote (bound to province change and zip blur) run exactly as they
   * do when a customer types.
   */
  /**
   * Shut the suggestion list after a pick.
   *
   * Places owns `.pac-container`'s inline display and sets it back to block on
   * the next real keystroke, so hiding it here is not sticky. Belt to the
   * silent-write braces above: the container can also linger when a pick is
   * made by mouse, because the blur that would normally close it never happens.
   */
  function closeSuggestions(inputId) {
    var input = $(inputId || 'order-form-register-street-address');
    var boxes = document.querySelectorAll('.pac-container');
    for (var i = 0; i < boxes.length; i++) boxes[i].style.display = 'none';
    if (input) input.blur();
  }

  /**
   * One autocomplete per address block. This was a singleton wired to the
   * payer's field ids; the gift needs a second one, so it takes a map of ids
   * and keeps its own bound flag. The two differ in exactly one place: whose
   * state an excluded pick blocks. A payer in Georgia cannot open a plan; a box
   * to Georgia cannot be sent. Both stop the order, but they say different things.
   */
  var PAYER_PLACES = {
    key: 'payer', street: 'order-form-register-street-address', unit: 'order-form-register-street-address-2',
    city: 'order-form-register-city', state: 'province', zip: 'order-form-register-zip',
    gateMsg: 'state-gate-message', blockedText: 'We can\'t serve ',
    onBlocked: function () { state.stateBlocked = true; },
    afterPick: function () { checkStateGate(); maybeQuoteTax(); }
  };
  var GIFT_PLACES = {
    key: 'gift', street: 'gift-street-address', unit: 'gift-street-address-2',
    city: 'gift-city', state: 'gift-province', zip: 'gift-zip',
    gateMsg: 'gift-state-gate-message', blockedText: 'We can\'t deliver to ',
    onBlocked: function () { state.giftStateBlocked = true; },
    afterPick: function () { checkGiftStateGate(); }
  };
  state.placesBoundFor = {};

  function bindPlaces() {
    bindPlacesTo(PAYER_PLACES);
    if (giftOn()) bindPlacesTo(GIFT_PLACES);
  }

  function bindPlacesTo(cfg) {
    if (ADDRESS.places === false) return;
    if (state.placesBoundFor[cfg.key]) return;
    var input = $(cfg.street);
    if (!input || !window.google || !google.maps || !google.maps.places) return;

    var autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['address'],
      componentRestrictions: { country: ADDRESS.placesCountry || 'us' },
      fields: ['address_components', 'formatted_address'],
    });

    // Enter selects a suggestion; it must not submit the step.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });

    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (!place || !place.address_components) return;

      var part = {};
      place.address_components.forEach(function (c) {
        c.types.forEach(function (t) { part[t] = { long: c.long_name, short: c.short_name }; });
      });

      var streetNumber = part.street_number ? part.street_number.long : '';
      var route = part.route ? part.route.long : '';
      var city = (part.locality || part.sublocality_level_1 || part.postal_town || {}).long || '';
      var stateCode = (part.administrative_area_level_1 || {}).short || '';
      var zip = (part.postal_code || {}).long || '';
      var unit = (part.subpremise || {}).long || '';

      /**
       * `silent` suppresses the `input` event.
       *
       * Places listens for `input` on the address field. Writing the tidied
       * street back and dispatching `input` looks to Places exactly like the
       * customer typing, so it re-queries and re-opens the suggestion list —
       * which then sits there after a pick and will not go away. Only the
       * address input needs this; the others are not watched.
       *
       * `change` still fires, which is what validation and the tax quote use.
       */
      var setVal = function (id, val, silent) {
        var el = $(id);
        if (!el || !val) return;
        el.value = val;
        if (!silent) el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      };

      // Street line without the unit — the Apt/Unit field is its own input.
      var street = (streetNumber + ' ' + route).trim();
      if (street) setVal(cfg.street, street, true);
      if (unit) setVal(cfg.unit, unit);
      setVal(cfg.city, city);
      setVal(cfg.zip, zip);

      var placed = setStateByCode(stateCode, cfg.state);
      if (!placed) {
        // No option for this state => a jurisdiction we do not serve. Say so
        // rather than leaving the customer with an empty State field and no
        // idea why their address did not stick.
        var sel = $(cfg.state);
        if (sel) {
          sel.selectedIndex = 0;
          sel.dataset.unsupportedState = stateCode || 'unknown';
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var msg = $(cfg.gateMsg);
        if (msg) msg.hidden = false;
        setError(cfg.state, 'Please choose a valid ' + (REGION.code === 'nz' ? 'region' : 'state or territory') + '.');
        cfg.onBlocked();
        applySubmitBlocks();
        closeSuggestions(cfg.street); // this path returns early — it needs the close too
        return;
      }

      closeSuggestions(cfg.street);
      cfg.afterPick();
      persist();
    });

    state.placesBoundFor[cfg.key] = true;
  }

  /** Mirrors checkStateGate for the box's destination. */
  function checkGiftStateGate() {
    var msg = $('gift-state-gate-message');
    var giftCode = codeOf('gift-province');
    var blocked = !!(giftOn() && giftCode && ALLOWED_STATE_CODES && ALLOWED_STATE_CODES.indexOf(giftCode) === -1);
    if (msg) msg.hidden = !blocked;
    state.giftStateBlocked = blocked;
    applySubmitBlocks();
    return !blocked;
  }
  if ($('gift-province')) $('gift-province').addEventListener('change', checkGiftStateGate);

  /**
   * Show or hide the gift fields. Off clears their errors and their gate, so
   * a recipient address someone abandoned can never block the order.
   */
  function syncGift() {
    var on = giftOn();
    var body = $('gift-fields');
    if (body) body.hidden = !on;
    if (on) {
      bindPlacesTo(GIFT_PLACES);
      checkGiftStateGate();
    } else {
      STEP_FIELDS.gift.forEach(function (id) { setError(id, ''); });
      var msg = $('gift-state-gate-message');
      if (msg) msg.hidden = true;
      state.giftStateBlocked = false;
      applySubmitBlocks();
    }
  }
  if ($('gift-toggle')) $('gift-toggle').addEventListener('change', function () { syncGift(); persist(); });

  // Maps may resolve before or after this controller runs, so handle both.
  window.initGooglePlaces = function () { window.__placesReady = true; bindPlaces(); };
  if (window.__placesReady) bindPlaces();

  /* ------------------------------------------------------------ chip rows */

  function bindChips(rowId, hiddenId, onPick) {
    var row = $(rowId);
    if (!row) return;
    row.addEventListener('click', function (e) {
      var chip = e.target.closest('.layaway-chip');
      if (!chip || chip.dataset.value === undefined) return;
      row.querySelectorAll('.layaway-chip').forEach(function (c) { c.classList.remove('is-selected'); });
      chip.classList.add('is-selected');
      var hidden = $(hiddenId);
      if (hidden) hidden.value = chip.dataset.value;
      setError(hiddenId, '');
      if (onPick) onPick(chip.dataset.value);
      persist();
    });
  }

  bindChips('rate-selector', 'order-form-weekly-payment-cents', function () {
    renderSchedule();
  });

  bindChips('frequency-selector', 'order-form-pay-frequency', function (v) {
    state.frequency = v;
    state.payday = null;
    var np = $('next-pay'); if (np) np.value = '';
    renderPaydayChips();
    renderSchedule();
  });

  /* --------------------------------------------------------- payday chips */

  /**
   * Wednesday, Thursday, Friday. US layaways are debited on those days only.
   *
   * Measured on the live US book with QA fixtures excluded: Wed/Thu/Fri clear at
   * 21.9/24.6/25.9% against Mon 18.9% and Tue 6.3%. The server enforces the same rule
   * independently (LAYAWAYUS-VERCEL/lib/payable-day.js) and snaps anything else forward,
   * so a cached cart cannot smuggle a Monday through — this list only keeps the customer
   * from being shown a date we would then quietly move.
   */
  /** How far ahead the first-payment chips reach. */
  var HORIZON_DAYS = RGN('horizonDays', 14);
  /** Days between today and the first offered payday (a bank batch needs lead time in some regions). */
  var FIRST_PAYDAY_OFFSET = RGN('firstPaydayOffsetDays', 0);

  function isPayableDay(d) {
    if (typeof REGION.payableDay === 'function') return !!REGION.payableDay(d);
    var g = d.getDay();
    return g === 3 || g === 4 || g === 5;
  }

  function candidateDates() {
    // Every payable day (Wed/Thu/Fri) within the next FORTNIGHT, today included when today
    // is itself payable.
    //
    // The window used to run fifteen slots, which reached five weeks out and filled the
    // step with dates most people cannot place. Two weeks is about as far ahead as anyone
    // can say "yes, that one" — and the frequency chip already decides the interval BETWEEN
    // payments, so a fortnightly or four-weekly customer needs no more reach here than a
    // weekly one.
    var out = [], cursor = addDays(startOfToday(), FIRST_PAYDAY_OFFSET), horizon = addDays(cursor, HORIZON_DAYS);
    if (isPayableDay(cursor)) out.push(new Date(cursor.getTime()));
    while (cursor < horizon) {
      cursor = addDays(cursor, 1);
      if (!isPayableDay(cursor)) continue;
      out.push(new Date(cursor.getTime()));
    }
    return out;
  }

  function renderPaydayChips() {
    var row = $('payday-chips');
    if (!row) return;
    var dates = candidateDates();
    row.innerHTML = '';
    dates.forEach(function (d) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'layaway-chip';
      b.dataset.value = fmtSlash(d);
      b.textContent = fmtLong(d);
      if (state.payday && fmtSlash(state.payday) === b.dataset.value) b.classList.add('is-selected');
      row.appendChild(b);
    });
    if (!row.dataset.bound) {
      row.dataset.bound = '1';
      row.addEventListener('click', function (e) {
        var chip = e.target.closest('.layaway-chip');
        if (!chip) return;
        row.querySelectorAll('.layaway-chip').forEach(function (c) { c.classList.remove('is-selected'); });
        chip.classList.add('is-selected');
        var np = $('next-pay'); if (np) np.value = chip.dataset.value;
        state.payday = parseSlash(chip.dataset.value);
        setError('next-pay', '');
        renderSchedule();
        persist();
      });
    }
  }

  /* ------------------------------------------------- schedule computation */

  /**
   * The regular installment: the weekly base times the cadence multiple.
   * Weekly $25, fortnightly $50 — always round, never a derived fraction.
   */
  /** The weekly rate in force: the customer's chip when the region offers one, else the plan base. */
  function rateCents() {
    var el = $('order-form-weekly-payment-cents');
    var picked = el ? parseInt(el.value, 10) : 0;
    return picked > 0 ? picked : (PLAN.installment_cents || 0);
  }

  /**
   * A region that owns its arithmetic (NZ/AU: layby-plan.js, byte-identical to the
   * order backend's copy) returns { installment_cents, count, final_cents } here and
   * the three functions below read it; otherwise the US maths applies unchanged.
   */
  function regionPlan() {
    if (typeof REGION.plan !== 'function') return null;
    try { return REGION.plan({ financed_cents: financedCents(), rate_cents: rateCents(), frequency: state.frequency, cadence: CADENCE[state.frequency] || CADENCE.weekly }, H) || null; }
    catch (e) { return null; }
  }

  function regularInstallmentCents() {
    var rp = regionPlan();
    if (rp) return rp.installment_cents || 0;
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    var base = rateCents();
    return base * (cad.days / 7);
  }

  /**
   * The amount the schedule must cover: sticker plus quoted tax, minus deposit.
   * taxCents stays in the arithmetic even though no tax is charged today (the
   * authorization copy makes no claim about tax in either direction):
   * if a real quote ever lands, the payments still have to sum to the server's
   * financed total (which finances tax) or verifyClientPlan refuses the order
   * as a PLAN_MISMATCH. Whoever wires up a tax provider must change the
   * authorization copy in main-cart.liquid at the same time.
   * (Tax quoting is currently unconfigured, so taxCents is null and this is
   * identical to sticker-minus-deposit.)
   */
  function financedCents() {
    return (PLAN.price_cents || 0) + (state.taxCents || 0) - (PLAN.deposit_cents || 0);
  }

  /** Payment count. The LAST payment absorbs the remainder, matching the contract schedule. */
  function installmentCount() {
    var rp = regionPlan();
    if (rp) return rp.count || 0;
    var financed = financedCents();
    var reg = regularInstallmentCents();
    return financed <= 0 || reg <= 0 ? 0 : Math.ceil(financed / reg);
  }

  function scheduleDates() {
    if (!state.payday || !PLAN.weeks) return [];
    var out = [], cad = CADENCE[state.frequency] || CADENCE.weekly;
    var n = installmentCount();
    var d = new Date(state.payday.getTime());
    for (var i = 0; i < n; i++) {
      out.push(new Date(d.getTime()));
      d = addDays(d, cad.days);
    }
    return out;
  }

  /** The amount shown to the customer — the regular installment. */
  function perInstallmentCents() {
    return regularInstallmentCents();
  }

  /** Final payment, which carries whatever the regular installments do not. */
  function finalInstallmentCents() {
    var rp = regionPlan();
    if (rp) return rp.final_cents || 0;
    var n = installmentCount();
    if (n <= 0) return 0;
    return financedCents() - regularInstallmentCents() * (n - 1);
  }

  function totalWithTax() {
    return (PLAN.price_cents || 0) + (state.taxCents || 0);
  }

  /**
   * The schedule moved. This used to paint a six-row recap card and a
   * "see every payment date" table into the schedule section; both are gone,
   * and the timeline — rendered by renderFinalSummary → renderTimeline — is
   * the one drawing of the plan. What is left here is the part that must
   * still run whenever the schedule moves: the totals, and the blocks below
   * that quote them.
   *
   * Every section is visible at once, so anything that moves the schedule
   * must move the authorization block and the timeline with it, or they
   * would quote a total the schedule no longer sums to. The payday chips set
   * their hidden input directly and fire no input event, so this — not a
   * listener — is what keeps the bottom of the form honest.
   */
  function renderSchedule() {
    updateSummaryTotals();
    renderFinalSummary();
  }

  function renderFinalSummary() {
    var dates = scheduleDates();
    var amt = perInstallmentCents();
    var el = $('final-summary');
    if (el) {
      el.innerHTML = dates.length
        ? '<p><strong>' + dueTodayLabel() + ' today.</strong> Your first payment of <strong>' +
          money(amt) + '</strong> comes out on ' + fmtLong(dates[0]) + ', then ' +
          ((CADENCE[state.frequency] || CADENCE.weekly).label) + ' — ' + dates.length +
          ' payments in all, paid off ' + fmtLong(dates[dates.length - 1]) + '.</p>'
        : '';
    }
    // authorization block
    var name = ($('order-form-register-f-name') || {}).value + ' ' + (($('order-form-register-l-name') || {}).value || '');
    var set = function (id, v) { var n = $(id); if (n) n.textContent = v; };
    set('auth-name', name.trim() || 'the customer named above');
    set('auth-deposit', dueTodayLabel());
    set('auth-count', dates.length || '—');
    set('auth-installment', money(amt));
    set('auth-cadence', (CADENCE[state.frequency] || CADENCE.weekly).label);
    set('auth-first-date', dates.length ? fmtLong(dates[0]) : '—');
    set('auth-final-date', dates.length ? fmtLong(dates[dates.length - 1]) : '—');
    set('auth-total', money(totalWithTax()));
    set('submit-deposit', dueTodayLabel());
    // prefill typed signature
    var sig = $('typed-signature');
    if (sig && !sig.value) sig.value = name.trim();
    renderTimeline();
  }

  /* ------------------------------------------------ plan-at-a-glance timeline
     The last thing the customer reads before "Start my layaway": what happens
     today, the first payments, the final one — and then the two events that
     only exist AFTER the money, the order shipping and arriving. Rendered into
     #plan-timeline at the foot of step 4.

     It reads the same scheduleDates()/installment figures the authorization
     block reads, so the two can never disagree. The ship date is an ESTIMATE:
     the ship-window setting ("14 days") counted forward from the
     final payment. When that setting is prose the code cannot count, the row
     quotes the window instead of inventing a date. Delivery gets no date at
     all — carriers, not us, decide that — so the arrival row is an event, not
     a promise.
     ---------------------------------------------------------------------- */

  /** How many payments show before the fold, and the plan length below which nothing folds. */
  var TIMELINE_HEAD_ROWS = 3;
  var TIMELINE_FOLD_FROM = 6;

  /** "$25" when there are no cents, "$12.50" when there are — the schedule table's money() shows cents always. */
  function moneyShort(cents) {
    if (cents == null || isNaN(cents)) return '—';
    if (cents % 100 !== 0) return money(cents);
    return '$' + String(cents / 100).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function addBusinessDays(d, n) {
    var x = new Date(d.getTime()), left = n;
    while (left > 0) {
      x = addDays(x, 1);
      var g = x.getDay();
      if (g !== 0 && g !== 6) left--;
    }
    return x;
  }

  /**
   * The ship window as a date, or null when the setting cannot be counted.
   * Understands "20 business days", "1–3 weeks", "3 weeks" and "10 days"; a
   * range takes its FAR end so the estimate errs late, never early.
   */
  function shipEstimate(from) {
    var w = String(PLAN.ship_window || '');
    var m;
    if ((m = /(\d+)\s*business/i.exec(w))) return addBusinessDays(from, +m[1]);
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*weeks?/i.exec(w))) return addDays(from, +m[2] * 7);
    if ((m = /(\d+)\s*weeks?/i.exec(w))) return addDays(from, +m[1] * 7);
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*days?/i.exec(w))) return addDays(from, +m[2]);
    if ((m = /(\d+)\s*days?/i.exec(w))) return addDays(from, +m[1]);
    return null;
  }

  function cadenceNoun(count) {
    if (state.frequency === 'weekly') return count + ' weekly payment' + (count === 1 ? '' : 's');
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    return count + ' payments ' + cad.label;
  }

  var TIMELINE_ICON = {
    check: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3.2 3L13 4.5"/></svg>',
    truck: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 3.5h8v7h-8zM9.5 6h3l2 2.5v2h-5z"/><circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none"/></svg>',
    home: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 7.5L8 3l5.5 4.5M4 6.8V13h8V6.8"/><path d="M6.8 13V9.5h2.4V13"/></svg>'
  };

  function timelineRow(o) {
    return '<li class="layaway-timeline__item' + (o.mod ? ' layaway-timeline__item--' + o.mod : '') + '"' +
      (o.folded ? ' data-folded hidden' : '') + '>' +
      '<span class="layaway-timeline__node" aria-hidden="true">' + (o.icon ? TIMELINE_ICON[o.icon] : '') + '</span>' +
      '<span class="layaway-timeline__when">' + o.when + '</span>' +
      (o.note ? '<span class="layaway-timeline__note">' + o.note + '</span>' : '') +
      (o.amt != null ? '<span class="layaway-timeline__amt">' + o.amt + '</span>' : '') +
      '</li>';
  }

  function renderTimeline() {
    var box = $('plan-timeline');
    if (!box) return;
    var dates = scheduleDates();
    if (!dates.length) { box.innerHTML = ''; return; }

    var n = dates.length;
    var amt = perInstallmentCents();
    var fin = finalInstallmentCents();
    var last = dates[n - 1];
    var folds = n >= TIMELINE_FOLD_FROM;
    var rows = [];

    rows.push(timelineRow({
      mod: 'today', icon: 'check', when: 'Today',
      note: 'Nothing is charged today.',
      amt: dueTodayLabel()
    }));

    dates.forEach(function (d, i) {
      var isLast = i === n - 1;
      if (folds && i === TIMELINE_HEAD_ROWS) {
        var hiddenCount = n - 1 - TIMELINE_HEAD_ROWS;
        rows.push('<li class="layaway-timeline__item layaway-timeline__item--fold">' +
          '<span class="layaway-timeline__node" aria-hidden="true"></span>' +
          '<button type="button" class="layaway-timeline__more" data-timeline-toggle aria-expanded="false"' +
          ' data-label-more="' + hiddenCount + ' more payments of ' + moneyShort(amt) + ' — show all"' +
          ' data-label-less="Show fewer">' + hiddenCount + ' more payments of ' + moneyShort(amt) + ' — show all</button></li>');
      }
      rows.push(timelineRow({
        mod: isLast ? 'final' : null,
        icon: isLast ? 'check' : null,
        when: isLast ? fmtShortYear(d) : fmtShort(d),
        note: i === 0 ? 'First payment' : (isLast ? 'Final payment — paid off' : null),
        amt: moneyShort(isLast ? fin : amt),
        folded: folds && i >= TIMELINE_HEAD_ROWS && !isLast
      }));
    });

    rows.push(timelineRow({
      mod: 'pace', icon: null,
      when: (REGION.timeline && REGION.timeline.paceWhen) || 'Want it sooner?',
      note: (REGION.timeline && REGION.timeline.paceNote) || 'Raise your payment in your account any time before it\'s due.'
    }));

    // No ship-by date and no window here: the goal the customer works to is
    // the payoff, and the one date after it is the one we do not control to
    // the day. Same rule as the homepage instrument.
    var TL = REGION.timeline || {};
    rows.push(timelineRow({
      mod: 'ship', icon: 'truck',
      when: TL.shipWhen || 'Ships free',
      note: TL.shipNote || 'We order it brand-new after your final payment.'
    }));
    rows.push(timelineRow({
      mod: 'home', icon: 'home',
      when: TL.homeWhen || 'At your door',
      note: TL.homeNote || 'Fully paid. Nothing more to pay, ever.'
    }));

    box.innerHTML =
      '<section class="layaway-timeline" aria-label="Your plan at a glance">' +
        '<div class="layaway-timeline__head">' +
          '<span class="layaway-timeline__hero">' + dueTodayLabel() + ' today</span>' +
          '<span class="layaway-timeline__meta">' + cadenceNoun(n) + ' · Paid off ' + fmtDateYear(last) + ' · ' + (TL.shipWhen || 'Ships free') + '</span>' +
        '</div>' +
        '<ol class="layaway-timeline__list">' + rows.join('') + '</ol>' +
      '</section>';
  }

  // Fold toggle. Bound once on the wrapper so re-renders need no re-binding.
  if ($('plan-timeline')) {
    $('plan-timeline').addEventListener('click', function (e) {
      var btn = e.target.closest('[data-timeline-toggle]');
      if (!btn) return;
      var open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      $('plan-timeline').querySelectorAll('[data-folded]').forEach(function (li) { li.hidden = !open; });
    });
  }

  /** "a week" / "a fortnight" beside the headline; a region may name its fortnight. */
  function cadenceWord() {
    var c = CADENCE[state.frequency] || CADENCE.weekly;
    if (c.days === 7) return 'a week';
    if (c.days === 14) return RGN('fortnightWord', 'every 2 weeks');
    return c.label;
  }

  /** The line under the headline: how many payments, and the first and last dates once a payday is picked. */
  function summaryMeta() {
    var n = installmentCount();
    if (!n) return '';
    var dates = scheduleDates();
    var s = n + ' payment' + (n === 1 ? '' : 's');
    if (dates.length) s += ' · first ' + fmtDateYear(dates[0]) + ' · paid off ' + fmtDateYear(dates[dates.length - 1]);
    return s;
  }

  function updateSummaryTotals() {
    var today = $('deposit-amount'); if (today) today.textContent = dueTodayLabel();
    var tot = $('order-total'); if (tot) tot.textContent = money(totalWithTax());
    var sd = $('submit-deposit'); if (sd) sd.textContent = dueTodayLabel();
    // the payment-first headline in the order summary
    var amt = $('summary-installment'); if (amt) amt.textContent = moneyShort(perInstallmentCents());
    var cad = $('summary-cadence'); if (cad) cad.textContent = cadenceWord();
    var meta = $('summary-meta'); if (meta) meta.textContent = summaryMeta();
  }

  /* ------------------------------------------------------------ tax quote */

  function maybeQuoteTax() {
    if (RGN('quoteTax', true) === false) return;
    var zip = ($('order-form-register-zip') || {}).value || '';
    var st = ($('province') || {}).value || '';
    // Excluded jurisdictions never appear in the <select>, so the only gate
    // needed here is "a state is chosen". (The old st === 'DC' check compared
    // codes against full-name option values and could never match.)
    if (!/^\d{5}$/.test(zip) || !st) return;
    var amountEl = $('tax-amount');
    if (amountEl) amountEl.textContent = 'Calculating…';

    fetch(CFG.API_BASE + '/api/quote-tax', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ zip: zip, state: st, items: cartItemsPayload() })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        // quote-tax responds with taxCents (camelCase); older builds of this
        // file read tax_cents only, which never matched, so a real quote could
        // never land even once the provider was wired. Accept both.
        var quoted = (typeof j.tax_cents === 'number') ? j.tax_cents
                   : (typeof j.taxCents === 'number') ? j.taxCents : null;
        if (quoted !== null && quoted >= 0) {
          state.taxCents = quoted;
          state.taxQuoteId = j.quote_id || j.quoteId || null;
          if (amountEl) amountEl.textContent = money(quoted);
        } else { throw new Error('bad quote'); }
        updateSummaryTotals();
        renderSchedule();
      })
      .catch(function () {
        // No quote (quote-tax is unconfigured and the server charges $0 tax).
        // The summary carries no tax row any more — a "no sales tax" line is
        // restricted in several of our states — so there is nothing to write.
        state.taxCents = null;
        if (amountEl) amountEl.textContent = '';
        updateSummaryTotals();
      });
  }

  /* ----------------------------------------------------------- cart items */

  function cartItemsPayload() {
    var cart = window.cartData || { items: [] };
    var totals = window.itemTotalValues || {};
    var amt = perInstallmentCents();
    return (cart.items || []).map(function (it) {
      var extra = totals[it.id] || {};
      var totalValue = it.final_line_price != null ? it.final_line_price : extra.total_value_cents;
      return {
        variant_id: it.variant_id || it.id,
        product_id: it.product_id,
        title: it.product_title || it.title,
        handle: extra.handle || it.handle,
        image: it.image,
        price: amt,
        total_value: totalValue,
        weeks: PLAN.weeks || 0,
        quantity: it.quantity,
        tags: extra.tags || [],
        properties: it.properties || {},
        replacement_transfer: false,
        promo_application: null
      };
    });
  }

  /* ------------------------------------------------------------ persisted */

  var PERSIST = ['order-form-register-email', 'order-form-register-phone', 'order-form-register-f-name',
    'order-form-register-l-name', 'dob', 'order-form-register-street-address',
    'order-form-register-street-address-2', 'order-form-register-city', 'province',
    'order-form-register-zip', 'order-form-pay-frequency', 'order-form-weekly-payment-cents', 'next-pay',
    'gift-f-name', 'gift-l-name', 'gift-phone', 'gift-street-address', 'gift-street-address-2',
    'gift-city', 'gift-province', 'gift-zip', 'gift-message'];

  var persistTimer = null;
  function persist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(function () {
      try {
        var data = {};
        PERSIST.forEach(function (id) { var el = $(id); if (el) data[id] = el.value; });
        data.__step = state.step;
        data.__gift = giftOn();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) { /* storage full or blocked */ }
    }, 400);
  }

  function restore() {
    var data;
    try { data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (e) { return; }
    PERSIST.forEach(function (id) {
      if (!data[id]) return;
      var el = $(id);
      if (!el || el.value) return;
      // stale-payday guard: never restore a past first-payment date — it could
      // ride into an order and trigger an immediate charge (AU incident).
      if (id === 'next-pay') {
        var d = parseSlash(data[id]);
        // Past dates were already rejected (an AU incident: a restored stale date rode
        // into an order and charged immediately). Carts saved before the Wed-Fri change
        // hold a Monday or Tuesday, so those are dropped too rather than restored into
        // a form that would now refuse them at step 3.
        if (!d || d < startOfToday() || !isPayableDay(d)) return;
        state.payday = d;
      }
      if (id === 'province') {
        var savedState = String(data[id]).trim().toLowerCase();
        var match = Array.prototype.find.call(el.options, function (option) {
          return option.value && (option.value.toLowerCase() === savedState ||
            (option.getAttribute('data-code') || '').toLowerCase() === savedState);
        });
        el.value = match ? match.value : '';
        if (!match) el.dataset.unsupportedState = data[id];
      } else el.value = data[id];
    });
    var rate = data['order-form-weekly-payment-cents'];
    if (rate) {
      var rrow = $('rate-selector');
      if (rrow) rrow.querySelectorAll('.layaway-chip').forEach(function (c) {
        c.classList.toggle('is-selected', c.dataset.value === rate);
      });
    }
    var freq = data['order-form-pay-frequency'];
    if (freq) {
      state.frequency = freq;
      var row = $('frequency-selector');
      if (row) row.querySelectorAll('.layaway-chip').forEach(function (c) {
        c.classList.toggle('is-selected', c.dataset.value === freq);
      });
    }
  }
  function clearPersisted() { try { localStorage.removeItem(STORAGE_KEY); } catch (e) {} }

  /* ---------------------------------------------------------- save-cart */

  // Capture must never prevent a customer from finishing the order.
  var checkoutCapture = {id:null,schedule:function(){},flush:function(){return Promise.resolve();},stop:function(){}};
  if (window.LayawayCheckoutCapture && !new URLSearchParams(location.search).has('resume_checkout')) {
    checkoutCapture = window.LayawayCheckoutCapture.create({
    endpoint: window.LayawayCheckoutAPI + '/capture', site: CFG.SITE,
    token: (window.cartData || {}).token,
    onToken: function (token) { if (window.cartData) window.cartData.token = token; },
    snapshot: function () {
      return {
        token: (window.cartData || {}).token, site: CFG.SITE,
        customer: {
          email: ($('order-form-register-email') || {}).value || '',
          phone: ($('order-form-register-phone') || {}).value || '',
          first_name: ($('order-form-register-f-name') || {}).value || '',
          last_name: ($('order-form-register-l-name') || {}).value || ''
        },
        items: cartItemsPayload().map(function (item) {
          return {variant_id:item.variant_id,quantity:item.quantity,total_value:item.total_value,
            title:item.title,handle:item.handle,image:item.image};
        }), step: state.step, attribution: attribution()
      };
    }
  });
  }
  form.addEventListener('input', checkoutCapture.schedule);
  form.addEventListener('change', checkoutCapture.schedule);
  form.addEventListener('focusout', function () { checkoutCapture.flush(); });
  checkoutCapture.schedule();

  /* ------------------------------------------------------------- attribution */

  function attribution() {
    try { return JSON.parse(localStorage.getItem('ld_attribution') || '{}'); } catch (e) { return {}; }
  }
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? m[1] : '';
  }

  /* ------------------------------------------------------------ Meta events */

  function metaTrack(event, payload) {
    try { if (window.fbq) window.fbq('track', event, payload || {}); } catch (e) {}
  }
  metaTrack('InitiateCheckout', {
    value: (PLAN.price_cents || 0) / 100,
    currency: CFG.CURRENCY || 'USD',
    num_items: (window.cartData && window.cartData.item_count) || 0
  });
  var firedAddPayment = false;
  form.addEventListener('focusin', function () {
    if (firedAddPayment) return;
    firedAddPayment = true;
    metaTrack('AddPaymentInfo', { value: (PLAN.price_cents || 0) / 100, currency: CFG.CURRENCY || 'USD' });
  });

  /* ------------------------------------------------------------- Stripe */

  function withStripe(cb) {
    if (window.Stripe) return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (window.Stripe) { clearInterval(t); cb(); }
      else if (++tries > 100) { clearInterval(t); showCardError('We couldn\'t load secure payments. Please refresh and try again.'); }
    }, 100);
  }

  function showCardError(msg) {
    var box = $('stripe-card-errors');
    if (box) box.textContent = msg || '';
  }

  /*
   * Ask the CRM whether this verified instrument would actually be ACCEPTED — before an
   * order exists. The vault gate (prepaid cards refused, failed CVC refused) used to run
   * only inside the order webhook, after Shopify had already taken the order: the customer
   * finished checkout believing they were set up, and the contract landed with nothing
   * able to charge it. Asking here lets them swap cards on the spot instead.
   *
   * FAIL OPEN on anything that is not an explicit refusal. A network blip or a CRM 5xx
   * must not strand a checkout — the webhook's gate is still the backstop — so only a
   * well-formed { ok: false } from the CRM blocks the order.
   */
  function verifyInstrument(setupIntentId) {
    return fetch(CFG.CARD_API_BASE + '/api/stripe/verify-instrument', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupIntentId: setupIntentId })
    })
      .then(function (r) { return r.ok ? r.json() : { ok: true, unverified: true }; })
      .then(function (j) {
        if (j && j.ok === false) return { ok: false, message: j.message || DECLINE_COPY.card_declined, reason: j.reason || null };
        return { ok: true };
      })
      .catch(function () { return { ok: true, unverified: true }; });
  }

  /*
   * A refused instrument cannot be re-confirmed — a succeeded SetupIntent is terminal — so
   * the customer needs a FRESH one to try another card. Tear the element down and let
   * mountStripe mint again; it early-returns while stripeMounted is true, hence the reset.
   */
  function remountStripe() {
    try {
      var el = state.elements && state.elements.getElement && state.elements.getElement('payment');
      if (el) el.unmount();
    } catch (err) {}
    state.elements = null;
    state.stripeMounted = false;
    window.__ladCardSetupIntentId = null;
    mountStripe();
  }

  var RAIL = RGN('rail', 'card');

  function mountStripe() {
    if (RAIL !== 'card') return;
    if (state.stripeMounted) return;
    var email = ($('order-form-register-email') || {}).value || '';
    if (!RE_EMAIL.test(email)) return;
    state.stripeMounted = true;
    showCardError('');

    withStripe(function () {
      fetch(CFG.CARD_API_BASE + '/api/stripe/setup-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (j) {
          if (!j.clientSecret) throw new Error('no client secret');
          // Prefer the publishable key the CRM served alongside the client_secret: it is
          // from the same Stripe account by construction, so the two cannot drift. The
          // hardcoded CFG.STRIPE_PK is the fallback for older CRM deploys that do not
          // return one. Stashed for the 3DS redirect return, which reloads this page with
          // no CRM round-trip to ask again.
          state.publishableKey = j.publishableKey || CFG.STRIPE_PK;
          try { sessionStorage.setItem(PK_KEY, state.publishableKey); } catch (e) {}
          state.stripe = window.Stripe(state.publishableKey);
          state.elements = state.stripe.elements({
            clientSecret: j.clientSecret,
            appearance: { theme: 'stripe', variables: { colorPrimary: '#e8641b', borderRadius: '6px' } }
          });
          state.elements.create('payment', {
            defaultValues: { billingDetails: { address: { country: 'US' } } }
          }).mount('#stripe-payment-element');
        })
        .catch(function () {
          state.stripeMounted = false; // allow retry
          showCardError('We couldn\'t start secure payment setup. Please try again in a moment.');
        });
    });
  }

  var DECLINE_COPY = {
    card_declined: 'Your bank didn\'t approve this card. Try again, or use a different card.',
    insufficient_funds: 'There isn\'t enough available on this card right now. Try another card.',
    expired_card: 'That card looks expired. Try a different card.',
    incorrect_cvc: 'The security code didn\'t match. Check it and try again.',
    prepaid_card: 'Prepaid cards can\'t be used for a payment plan. Please use a bank account, or a debit or credit card from your bank.',
    processing_error: 'Something went wrong on the bank\'s end. Please try again.',
    // ACH / Financial Connections. Every one of these ends by pointing at card, because
    // card is always available and is the default option — a customer whose bank cannot
    // be linked should never be left thinking they cannot buy.
    payment_method_not_available: 'We couldn\'t connect to that bank. Please try another account, or pay by card.',
    bank_account_unusable: 'That account can\'t be used for payments. Please try another account, or pay by card.',
    bank_account_restricted: 'That account has restrictions that block automatic payments. Please try another account, or pay by card.',
    debit_not_authorized: 'That account isn\'t authorized for automatic payments. Please try another account, or pay by card.'
  };
  function friendlyCardError(err) {
    if (!err) return 'We couldn\'t verify those payment details. Please try again.';
    if (err.decline_code && DECLINE_COPY[err.decline_code]) return DECLINE_COPY[err.decline_code];
    if (err.code && DECLINE_COPY[err.code]) return DECLINE_COPY[err.code];
    return err.message || 'We couldn\'t verify those payment details. Please try again.';
  }

  /* ---------------------------------------------------------- payload */

  var v = function (id) { var el = $(id); return el ? (el.value || '').trim() : ''; };
  var checked = function (id) { var el = $(id); return !!(el && el.checked); };

  function buildPayload() {
    var payload = buildOrderPayload();
    payload.formDetails = payload.formDetails || {};
    payload.formDetails.checkout_id = checkoutCapture.getId ? checkoutCapture.getId() : checkoutCapture.id;
    payload.formDetails.cart_token = (window.cartData || {}).token || '';
    return payload;
  }
  function buildOrderPayload() {
    if (typeof REGION.buildPayload === 'function') return REGION.buildPayload(H);
    var dates = scheduleDates();
    return {
      site: CFG.SITE,
      customer: {
        first_name: v('order-form-register-f-name'),
        last_name: v('order-form-register-l-name'),
        email: v('order-form-register-email'),
        phone: v('order-form-register-phone'),
        address: v('order-form-register-street-address'),
        address2: v('order-form-register-street-address-2'),
        city: v('order-form-register-city'),
        state: v('province'),
        postcode: v('order-form-register-zip')
      },
      items: cartItemsPayload(),
      // `customer` is always the signer. `delivery` is where the box goes,
      // and is only sent when it is somebody else's address; create-order
      // treats its absence as "deliver to the signer", which is what every
      // order before the gift did.
      delivery: giftOn() ? {
        first_name: v('gift-f-name'),
        last_name: v('gift-l-name'),
        phone: v('gift-phone'),
        address: v('gift-street-address'),
        address2: v('gift-street-address-2'),
        city: v('gift-city'),
        state: v('gift-province'),
        postcode: v('gift-zip')
      } : null,
      gift: giftOn() ? {
        is_gift: true,
        recipient_first_name: v('gift-f-name'),
        recipient_last_name: v('gift-l-name'),
        recipient_phone: v('gift-phone'),
        message: v('gift-message')
      } : null,
      formDetails: {
        date_of_birth: v('dob'),
        gender: '',
        next_pay_date: v('next-pay'),
        pay_frequency: v('order-form-pay-frequency') || state.frequency,
        promo_code: v('promo-code'),
        affiliate_referral_code: (function () {
          try { return localStorage.getItem('layaway_affiliate_code') || ''; } catch (e) { return ''; }
        })(),
        attribution: attribution(),
        // E-SIGN consent. create-order refuses the order without it, so it rides
        // in the same payload as the other three agreement boxes.
        agreed_to_electronic_records: checked('agreed_to_electronic_records'),
        agreed_to_terms: checked('agreed_to_terms'),
        agreed_to_charges: false,
        agreed_to_cancellation: checked('agreed_to_cancellation'),
        agreed_to_payment_info: checked('agreed_to_payment_info'),
        confirmed_affordability: false,
        subscribe_to_mailing: checked('order-subscribe-to-mailing-list'),
        sms_consent: checked('sms-consent'),
        is_bundle: (window.cartData && window.cartData.item_count > 1) || false,
        bundle_item_count: (window.cartData && window.cartData.item_count) || 0,
        payment_method: 'card',
        stripe_setup_intent_id: window.__ladCardSetupIntentId || '',
        signature: v('typed-signature'),
        // Drawn signature as a PNG dataURL. The NZ/AU webhook already accepts
        // 'Electronic Signature' dataURLs and uploads them to Blob, so this
        // matches the existing backend contract rather than inventing one.
        signature_image: (sigPad && sigPad.dataUrl()) || '',
        signature_signed_at: new Date().toISOString(),
        submitted_at: new Date().toISOString(),
        // Kept for the backend contract; the plan JSON reports 0 and nothing is
        // collected at signup — the card is only verified.
        deposit_cents: PLAN.deposit_cents,
        installment_cents: perInstallmentCents(),
        installment_count: dates.length,
        final_payment_date: dates.length ? fmtSlash(dates[dates.length - 1]) : '',
        tax_cents: state.taxCents,
        tax_quote_id: state.taxQuoteId,
        estimated_delivery_window: PLAN.ship_window || ''
      },
      weekly_payment_cents: perInstallmentCents(),
      fbp: cookie('_fbp'),
      fbc: cookie('_fbc'),
      client_ip: '',
      event_id: 'lw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)
    };
  }


  /** For regions whose step 4 has no typed name: is there ink on the pad? */
  function padHasInk() { return !!(sigPad && sigPad.hasInk()); }

  /* --------------------------------------------------------- signature pad
     Hand-drawn signature, no CDN dependency. Pointer Events cover mouse,
     touch and stylus in one path. The canvas is sized to its CSS box * DPR so
     strokes are sharp on retina, and re-sized on rotate/resize WITHOUT losing
     existing ink (we replay the stroke list rather than scaling a bitmap).
     Exported as a trimmed PNG dataURL into order[signature_image]. */

  var sigPad = (function () {
    var canvas = $('signature-pad');
    if (!canvas) return null;

    var ctx = canvas.getContext('2d');
    var frame = canvas.parentNode;
    var hidden = $('signature-image');
    var statusEl = $('sigpad-status');
    var strokes = [];       // [[{x,y},...], ...] in CSS pixels
    var current = null;
    var drawing = false;

    function dpr() { return window.devicePixelRatio || 1; }

    function resize() {
      var r = canvas.getBoundingClientRect();
      if (!r.width) return;
      canvas.width = Math.round(r.width * dpr());
      canvas.height = Math.round(r.height * dpr());
      redraw();
    }

    function redraw() {
      var d = dpr();
      ctx.setTransform(d, 0, 0, d, 0, 0);
      ctx.clearRect(0, 0, canvas.width / d, canvas.height / d);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#2b1f1a';
      strokes.forEach(function (pts) {
        if (pts.length < 2) {
          // a single tap still deserves a visible dot
          if (pts.length === 1) {
            ctx.beginPath();
            ctx.arc(pts[0].x, pts[0].y, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = '#2b1f1a';
            ctx.fill();
          }
          return;
        }
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke();
      });
    }

    function pos(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function start(e) {
      drawing = true;
      current = [pos(e)];
      strokes.push(current);
      if (canvas.setPointerCapture && e.pointerId != null) {
        try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
    }

    function move(e) {
      if (!drawing) return;
      current.push(pos(e));
      redraw();
      e.preventDefault();
    }

    function end() {
      if (!drawing) return;
      drawing = false;
      current = null;
      commit();
    }

    function hasInk() {
      // a stray tap is not a signature — require real travel
      var pts = 0;
      for (var i = 0; i < strokes.length; i++) pts += strokes[i].length;
      return strokes.length > 0 && pts >= 8;
    }

    function commit() {
      frame.classList.toggle('has-ink', hasInk());
      if (hidden) hidden.value = hasInk() ? canvas.toDataURL('image/png') : '';
      if (statusEl) {
        statusEl.textContent = hasInk() ? 'Signature captured' : '';
        statusEl.classList.toggle('is-signed', hasInk());
      }
      if (hasInk()) setError('sigpad-error', '');
    }

    canvas.addEventListener('pointerdown', start);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('pointerleave', end);

    var clearBtn = $('sigpad-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        strokes = [];
        redraw();
        commit();
      });
    }

    window.addEventListener('resize', resize);
    resize();

    return {
      hasInk: hasInk,
      dataUrl: function () { return hasInk() ? canvas.toDataURL('image/png') : ''; },
      clear: function () { strokes = []; redraw(); commit(); },
      resize: resize
    };
  })();

  /* ------------------------------------------------------------- submit */

  function showFormErrors(messages, title) {
    var modal = $('form-errors-modal');
    var list = $('form-errors-list');
    var head = $('form-errors-title');
    // The title is settable so a reassuring notice is not shown under
    // "Please fix these to continue" — which is what exit-intent used to do.
    if (head) head.textContent = title || 'Please fix these to continue';
    if (modal) modal.setAttribute('role', title ? 'dialog' : 'alertdialog');
    if (list) list.innerHTML = messages.map(function (m) { return '<li>' + m + '</li>'; }).join('');
    if (modal) modal.hidden = false;
  }
  function closeFormErrors() {
    var m = $('form-errors-modal');
    if (m) m.hidden = true;
  }
  // querySelectorAll, not querySelector: the overlay AND the OK button both
  // carry data-close-errors, and the overlay comes first in the markup.
  Array.prototype.forEach.call(
    document.querySelectorAll('#form-errors-modal [data-close-errors]'),
    function (el) { el.addEventListener('click', closeFormErrors); }
  );
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeFormErrors();
  });

  /* The label while the order posts. A region file may supply its own
     ("Starting your layby…"); the US keeps the shell's default. */
  function submittingLabel() {
    var R = window.LAYAWAY_REGION || {};
    return R.submittingLabel || 'Starting your plan…';
  }

  function setSubmitting(on, label) {
    state.submitting = on;
    var btn = $('btn-submit');
    if (!btn) return;
    applySubmitBlocks();
    var txt = btn.querySelector('.btn__text');
    if (txt && label) txt.textContent = label;
  }

  function postOrder(payload) {
    return fetch(CFG.API_BASE + '/api/create-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, body: j }; });
    });
  }

  function onOrderSuccess(payload, body) {
    checkoutCapture.stop();
    clearPersisted();
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
    // The order now lives in Shopify; the cart it came from is spent. Left
    // full, a back-navigation lands on the same form with the same items and a
    // verified card — one tap from an accidental duplicate order.
    try { fetch('/cart/clear.js', { method: 'POST' }); } catch (e) {}
    if (typeof REGION.beforePurchaseTrack === 'function') { try { REGION.beforePurchaseTrack(payload, body, H); } catch (e) {} }
    metaTrack('Purchase', {
      value: totalWithTax() / 100,
      currency: CFG.CURRENCY || 'USD',
      // metaEventId is the id the server's Conversions API Purchase carried
      // (the bare Shopify order id). Meta collapses a browser and server event
      // sharing (event_name, event_id) into ONE conversion — reading the wrong
      // field here meant the pair never deduped and every sale counted twice.
      eventID: (body && (body.metaEventId || body.event_id)) || payload.event_id
    });
    var dates = scheduleDates();
    var set = function (id, v) { var n = $(id); if (n) n.textContent = v; };
    set('thankyou-deposit', dueTodayLabel());
    set('thankyou-installment', money(perInstallmentCents()));
    set('thankyou-first-date', dates.length ? fmtLong(dates[0]) : '—');
    set('thankyou-email', payload.customer.email);
    var overlay = $('thank-you-overlay');
    if (overlay) { overlay.hidden = false; window.scrollTo({ top: 0, behavior: 'smooth' }); }
    if (typeof REGION.onSuccess === 'function') { try { REGION.onSuccess(payload, body, H); } catch (e) {} }
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!form.layawayReady || form.layawayAbandoned || state.submitting || state.emptyCart) return;

    clearErrors();
    var allOk = [1, 2, 3, 4].every(function (n) { return validateStep(n); }) && (!giftOn() || validateStep('gift'));
    if (!allOk || !checkStateGate()) {
      showFormErrors(['Please fix the highlighted fields before you continue.']);
      return;
    }
    if (PLAN.order_cap_cents && (PLAN.price_cents || 0) > PLAN.order_cap_cents) {
      showFormErrors(['Your order is above our current limit of ' + money(PLAN.order_cap_cents) + '. Remove an item to continue.']);
      return;
    }

    var payload = buildPayload();

    if (RAIL !== 'card') {
      // Direct debit: the authority is the signature and the ticked boxes; the
      // bank account rides in the payload and the order backend does the rest.
      setSubmitting(true, submittingLabel());
      postOrder(payload).then(function (res) {
        if (res.ok && res.body && res.body.success !== false) onOrderSuccess(payload, res.body);
        else {
          setSubmitting(false, submitLabel());
          showFormErrors([(res.body && res.body.message) || 'We couldn\'t start your plan. Please try again.']);
        }
      }).catch(function () {
        setSubmitting(false, submitLabel());
        showFormErrors(['Something went wrong sending your details. Please try again.']);
      });
      return;
    }

    // Two-pass gate: verify the card first, then submit with the setup intent.
    if (!window.__ladCardSetupIntentId) {
      if (!state.stripe || !state.elements) {
        showFormErrors(['Payment details aren\'t ready yet. Give it a moment and try again.']);
        return;
      }
      setSubmitting(true, 'Verifying your payment details…');
      // Stash before confirmSetup — a redirect-based 3DS leaves this page.
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload)); } catch (err) {}

      state.stripe.confirmSetup({
        elements: state.elements,
        confirmParams: { return_url: window.location.origin + window.location.pathname + '?stripe_return=1' },
        redirect: 'if_required'
      }).then(function (result) {
        if (result.error) {
          setSubmitting(false, submitLabel());
          showCardError(friendlyCardError(result.error));
          try { sessionStorage.removeItem(PENDING_KEY); } catch (err) {}
          return;
        }
        // Only a SUCCEEDED setup may go on to create the order. Anything else means the
        // payment method is not usable yet — a bank account awaiting microdeposit
        // verification is the realistic case — and the CRM would refuse to vault it,
        // leaving a live contract with nothing able to debit it while the customer
        // believed they had finished. Say so instead, and let them use a card.
        if (!result.setupIntent || result.setupIntent.status !== 'succeeded') {
          setSubmitting(false, submitLabel());
          showCardError(
            'We couldn\'t finish verifying that account. Please try a different bank, or pay by card.'
          );
          try { sessionStorage.removeItem(PENDING_KEY); } catch (err) {}
          return;
        }
        var intentId = result.setupIntent.id;
        setSubmitting(true, 'Checking your payment method…');
        return verifyInstrument(intentId).then(function (verdict) {
          if (!verdict.ok) {
            // Refused before any order exists. Say why, and hand them a fresh element so
            // the next card goes through the same check.
            setSubmitting(false, submitLabel());
            try { sessionStorage.removeItem(PENDING_KEY); } catch (err) {}
            showCardError(verdict.message);
            remountStripe();
            return;
          }
          window.__ladCardSetupIntentId = intentId;
          payload.formDetails.stripe_setup_intent_id = intentId;
          setSubmitting(true, submittingLabel());
          return postOrder(payload).then(function (res) {
            if (res.ok && res.body && res.body.success !== false) {
              onOrderSuccess(payload, res.body);
            } else {
              setSubmitting(false, submitLabel());
              showFormErrors([(res.body && res.body.message) || 'We couldn\'t start your plan. Please try again.']);
            }
          });
        });
      }).catch(function () {
        setSubmitting(false, submitLabel());
        showCardError('We couldn\'t verify those payment details. Please try again.');
      });
      return;
    }

    setSubmitting(true, submittingLabel());
    postOrder(payload).then(function (res) {
      if (res.ok && res.body && res.body.success !== false) onOrderSuccess(payload, res.body);
      else {
        setSubmitting(false, submitLabel());
        showFormErrors([(res.body && res.body.message) || 'We couldn\'t start your plan. Please try again.']);
      }
    });
  });

  /* ------------------------------------------------- 3DS redirect return */

  function handleStripeReturn() {
    var params = new URLSearchParams(window.location.search);
    var si = params.get('setup_intent');
    var secret = params.get('setup_intent_client_secret');
    if (!si || !secret) return;

    var clean = function () {
      history.replaceState({}, '', window.location.pathname);
    };
    var pending = null;
    try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch (e) {}

    withStripe(function () {
      // The key mountStripe() stashed — the account that minted this SetupIntent.
      // CFG.STRIPE_PK is the fallback for a session that predates the stash.
      var pk = null;
      try { pk = sessionStorage.getItem(PK_KEY); } catch (e) {}
      var stripe = window.Stripe(pk || CFG.STRIPE_PK);
      stripe.retrieveSetupIntent(secret).then(function (res) {
        var intent = res.setupIntent;
        if (intent && intent.status === 'succeeded' && pending) {
          // The redirect reloaded the page onto step 1. Put the customer back
          // on the payment step FIRST, so the submit progress — and any error —
          // appears where they left off rather than floating over "About you".
          goToStep(4);
          setSubmitting(true, 'Checking your payment method…');
          verifyInstrument(intent.id).then(function (verdict) {
            if (!verdict.ok) {
              clean();
              try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
              setSubmitting(false, submitLabel());
              showCardError(verdict.message);
              remountStripe();
              return;
            }
            window.__ladCardSetupIntentId = intent.id;
            pending.formDetails.stripe_setup_intent_id = intent.id;
            setSubmitting(true, submittingLabel());
            return postOrder(pending).then(function (r) {
              clean();
              if (r.ok && r.body && r.body.success !== false) onOrderSuccess(pending, r.body);
              else {
                setSubmitting(false, submitLabel());
                showFormErrors([(r.body && r.body.message) || 'We couldn\'t start your plan. Please try again.']);
              }
            });
          });
        } else if (intent && intent.status === 'succeeded') {
          // The bank check SUCCEEDED but the stashed payload is gone (storage
          // cleared, private browsing, different tab). The card is verified and
          // vaulted — telling the customer the check failed, as this branch used
          // to, is wrong twice over: it maligns a good card and walks them into
          // verifying it a second time. Keep the verified intent and ask only
          // for the re-submit.
          clean();
          goToStep(4);
          verifyInstrument(intent.id).then(function (verdict) {
            if (!verdict.ok) {
              showCardError(verdict.message);
              remountStripe();
              return;
            }
            window.__ladCardSetupIntentId = intent.id;
            showFormErrors(
              ['Your card is verified. Please review your details and press the button again to start your plan.'],
              'One more step'
            );
          });
        } else {
          clean();
          try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
          goToStep(4);
          showCardError('Your bank didn\'t complete the card check. Please try again or use a different card.');
        }
      }).catch(clean);
    });
  }

  /* ------------------------------------------------------- exit intent */

  function bindExitIntent() {
    var shown = false;
    try { shown = sessionStorage.getItem(EXIT_KEY) === '1'; } catch (e) {}
    if (shown) return;
    document.addEventListener('mouseout', function (e) {
      if (e.clientY > 0 || shown) return;
      var email = ($('order-form-register-email') || {}).value || '';
      if (!RE_EMAIL.test(email)) return;
      shown = true;
      try { sessionStorage.setItem(EXIT_KEY, '1'); } catch (err) {}
      showFormErrors(
        ['Your progress is saved on this device — come back any time and pick up where you left off.'],
        'Saved for later'
      );
    });
  }

  /* ------------------------------------------------------ cart changes */

  /**
   * The plan JSON was rendered by Liquid for the cart as it was at page load.
   * cart.js re-renders the items block on every quantity change or remove and
   * then fires cart:updated with the new cart; this re-derives the plan from
   * the new total using the same arithmetic as snippets/layaway-plan.liquid,
   * so the schedule, the authorization and the totals all move together.
   *
   * PLAN is mutated in place on purpose: dozens of functions read PLAN.x and
   * re-binding a fresh object would mean threading it through every one.
   */
  function replan(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    var p = Math.max(0, Math.round(cart.total_price));
    var pct = PLAN.deposit_pct || 0;
    var dep = 0;
    if (pct > 0) {
      dep = Math.floor(p * pct / 100);
      if (dep < (PLAN.deposit_floor_cents || 0)) dep = PLAN.deposit_floor_cents || 0;
      dep = Math.ceil(dep / 100) * 100;
      if (dep > p) dep = p;
    }
    var inst = PLAN.weekly_base_cents || PLAN.installment_cents || 0;
    var rem = p - dep;
    PLAN.price_cents = p;
    PLAN.deposit_cents = dep;
    PLAN.installment_cents = inst;
    PLAN.weekly_equiv_cents = inst;
    PLAN.weeks = (rem <= 0 || inst <= 0) ? 0 : Math.ceil(rem / inst);

    window.cartData = cart;
    var sub = $('cart-subtotal');
    if (sub && typeof cart.items_subtotal_price === 'number') sub.textContent = money(cart.items_subtotal_price);

    state.capBlocked = !!(PLAN.order_cap_cents && p > PLAN.order_cap_cents);
    state.emptyCart = p <= 0 || !cart.items || !cart.items.length;
    var w = $('order-cap-warning');
    if (w) w.hidden = !state.capBlocked;
    applySubmitBlocks();

    updateSummaryTotals();
    renderSchedule();
    maybeQuoteTax();
  }

  document.addEventListener('cart:updated', function (e) {
    replan(e && e.detail && e.detail.cart);
    checkoutCapture.schedule();
  });

  /* ------------------------------------------------------------- init */

  /**
   * The authorization block quotes the customer's own name and dates back to
   * them. It used to be rendered once, on entering step 4; on one page it has
   * to keep up with the fields above it.
   */
  var summaryTimer = null;
  function scheduleSummary() {
    clearTimeout(summaryTimer);
    summaryTimer = setTimeout(renderFinalSummary, 200);
  }

  function init() {
    submitLabel(); // capture the button's rendered label before any submit state overwrites it
    restore();
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (saved.__gift && $('gift-toggle')) $('gift-toggle').checked = true;
    } catch (e) {}
    syncGift();
    checkStateGate();
    renderPaydayChips();
    var cart = window.cartData;
    if (!cart || !Number.isFinite(cart.total_price) || !Array.isArray(cart.items)) {
      throw new Error('Checkout cart data is missing or invalid');
    }
    replan(cart);
    renderFinalSummary();
    bindExitIntent();
    handleStripeReturn();
    if (PLAN.order_cap_cents && (PLAN.price_cents || 0) > PLAN.order_cap_cents) {
      var w = $('order-cap-warning');
      if (w) w.hidden = false;
      state.capBlocked = true;
      applySubmitBlocks();
    }
    // The canvas is visible from the first paint now, so it can size itself.
    if (sigPad) sigPad.resize();
    form.addEventListener('input', scheduleSummary);
    form.addEventListener('change', scheduleSummary);
    // Stripe used to mount on entering step 4. It refuses to mount without a
    // valid email and returns without arming itself, so it is safe to try on
    // every keystroke and it arms the moment the address is complete.
    var email = $('order-form-register-email');
    if (email) {
      email.addEventListener('input', mountStripe);
      email.addEventListener('blur', mountStripe);
      if (email.value) mountStripe();
    }
    if (typeof REGION.init === 'function') REGION.init(H);
  }

  /* What a region hook may use. Declared with `var` so the functions above are
     already hoisted; filled here so the object is complete before init runs. */
  var H = {
    $: $, v: v, checked: checked, money: money, moneyShort: moneyShort,
    fmtSlash: fmtSlash, parseSlash: parseSlash, fmtLong: fmtLong, fmtShort: fmtShort,
    scheduleDates: scheduleDates, perInstallmentCents: perInstallmentCents,
    finalInstallmentCents: finalInstallmentCents, installmentCount: installmentCount,
    financedCents: financedCents, totalWithTax: totalWithTax, rateCents: rateCents,
    cartItemsPayload: cartItemsPayload, attribution: attribution, cookie: cookie,
    setError: setError, showFormErrors: showFormErrors, metaTrack: metaTrack,
    renderSchedule: renderSchedule, persist: persist, giftOn: giftOn,
    PLAN: PLAN, state: state, CFG: CFG, CADENCE: CADENCE,
    sigPad: function () { return sigPad; }, padHasInk: padHasInk
  };

  function initialize() {
    if (form.layawayAbandoned) return;
    try {
      init();
      window.LayawayForm = { form: form, state: state, plan: PLAN, goToStep: goToStep, buildPayload: buildPayload, helpers: H };
      form.layawayReady = true;
      applySubmitBlocks();
      form.dispatchEvent(new Event('layaway:ready'));
    } catch (err) {
      if (form.layawayGuard) form.layawayGuard.fail();
      console.error('[layaway] initialization failed', err);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
